import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H6 from '@base/H6/H6';
import BookIcon from '@icon/BookIcon';
import NotesFilled from '@icon/NotesFilled';
import { Spacings, breakpoints, Fonts } from '@styles/index';
import P2 from '@base/P2/P2';
import { pluralize } from '@utils/textHelper';

const LIVE = 1;

const Flex = styled.div`
  display: flex;
  align-items: center;
  grid-column: span 5;
  margin-top: ${Spacings.SPACING_8B};
  margin-right: 104px;
  @media (max-width: 47.95em) {
    margin-top: 24px;
    grid-column: span 10;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: var(--color-divider);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 40px;
    height: 40px;
  }
`;

const Info = styled.div`
  margin-left: ${Spacings.SPACING_6B};
  align-self: center;
  color: var(--color-text-primary);
  font-size: ${Fonts.P1};
  font-weight: ${Fonts.SEMIBOLD};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: ${Spacings.SPACING_3B};
    & > h6 {
      font-size: 14px;
    }
    & > p {
      color: var(--color-text-secondary);
    }
  }
`;

const CountInfo = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const getClassesCount = (classCount) => {
  const updatedClassCount = { live: 0, recorded: 0 };
  classCount.forEach((element) => {
    if (element.classType === LIVE) {
      updatedClassCount.live = element.total;
    } else {
      updatedClassCount.recorded = element.total;
    }
  });

  return updatedClassCount;
};

const SessionCount = ({
  lessonCount = 0,
  quizCount = 0,
  classCount = [],
  className,
  isTestSeries,
  infoString,
  isHybridNonOfflineCourse
}) => {
  if (!infoString && !lessonCount && !quizCount) return null;
  const updatedClassCount = getClassesCount(classCount);

  const getTestSeriesInfo = () => {
    return (
      <>
        <H6 color="var(--color-text-primary)">
          {lessonCount > 0 && pluralize(lessonCount, 'lesson')}
          {quizCount > 0 && lessonCount > 0 && ','}{' '}
          {quizCount > 0 && pluralize(quizCount || 0, 'test')}
        </H6>
      </>
    );
  };

  const getCourseInfo = () => {
    if (isHybridNonOfflineCourse) {
      return (
        <>
          <H6 color="var(--color-text-primary)">
            {`${updatedClassCount.live} live `}
            <span>&#183;</span>
            {` ${updatedClassCount.recorded} recorded`}
          </H6>
          <CountInfo>Lessons</CountInfo>
        </>
      );
    }

    if (lessonCount === 0) {
      return (
        <H6 color="var(--color-text-primary)">
          {pluralize(quizCount, 'quiz', 'zes')}
        </H6>
      );
    }
    return (
      <>
        <H6 color="var(--color-text-primary)">
          {pluralize(lessonCount || 0, 'lesson')}
        </H6>
        {quizCount > 0 && (
          <CountInfo>{pluralize(quizCount, 'quiz', 'zes')}</CountInfo>
        )}
      </>
    );
  };

  const getInfoString = () => {
    if (infoString) {
      return infoString;
    }
    return isTestSeries ? getTestSeriesInfo() : getCourseInfo();
  };

  return (
    <Flex className={className}>
      <IconWrapper>
        {isTestSeries ? (
          <NotesFilled
            size={Spacings.SPACING_6B}
            color="var(--color-text-secondary)"
          />
        ) : (
          <BookIcon
            size={Spacings.SPACING_12B}
            color="var(--color-text-secondary)"
            viewBox="0 0 48 48"
          />
        )}
      </IconWrapper>
      <Info>{getInfoString()}</Info>
    </Flex>
  );
};

export default SessionCount;

SessionCount.propTypes = {
  lessonCount: PropTypes.number,
  quizCount: PropTypes.number,
  className: PropTypes.string,
  isTestSeries: PropTypes.bool,
  infoString: PropTypes.string
};

SessionCount.defaultProps = {
  lessonCount: 0,
  quizCount: 0,
  className: '',
  infoString: null,
  isTestSeries: false
};
